import { useCallback, useRef, useEffect } from 'react';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  ModalFooter,
} from '@polichat/flamboyant';
import SimpleChannelSelector from './SimpleChannelSelector';
import { QuickMessageSelectorV2 } from './quickMessages/v2';
import { useDispatch, useSelector } from 'react-redux';
import { getMessageBuilderTemplateText } from './quickMessages/functions/getMessageBuilderTemplateText';
import './task.css';
import { toggleQuickMessagesSelector } from 'src/store/modules/chat/actions';
import { useGlotaT } from 'src/hooks/useGlotaTranslation';

const TaskSecondStep = ({
  date,
  time,
  handleChange,
  nextStep,
  prevStep,
  step,
  cancelScheduling,
  handleSubmit,
  messageScheduling,
  setMessageScheduling,
  setTemplateSelectedUuid,
}) => {
  const dateRef = useRef(null);
  const timeRef = useRef(null);

  const chat = useSelector((state) => state?.chat ?? {});

  const dispatch = useDispatch();

  const quickMessagesIsOpen = chat.quickMessages?.isOpen;

  const selectedChannel = useSelector((state) => {
    const chat =
      state?.chat?.chats?.filter((chat) => {
        return chat.open === true;
      })[0] || [];

    return chat?.view?.selectedChannel ?? null;
  });

  useEffect(() => {
    if (selectedChannel?.type === 'waba') {
      dispatch(toggleQuickMessagesSelector('open'));
    }
    if (selectedChannel?.type !== 'waba') {
      dispatch(toggleQuickMessagesSelector('close'));
    }
  }, [selectedChannel]);

  // Função auxiliar para obter a data atual em formato YYYY-MM-DD
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Função auxiliar para obter a hora atual no formato HH:MM
  const getCurrentTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Verificação se a data e hora são válidas
  const isDateTimeValid = (date, time) => {
    if (!date || !time) {
      return false; // Se algum dos campos não estiver preenchido, retorna false
    }
    const selectedDate = new Date(`${date}T${time}`);
    const now = new Date();
    return selectedDate > now;
  };

  const receiveSelectedQuickMessage = useCallback(
    (quickMessage) => {
      setMessageScheduling(getMessageBuilderTemplateText(quickMessage.text));
      setTemplateSelectedUuid(quickMessage.uuid);
    },
    [quickMessagesIsOpen]
  );

  const titleText = useGlotaT('spa.chat.scheduling.title');
  const defineDateTimeText = useGlotaT('spa.chat.scheduling.defineDateTime');
  const reviewInformationText = useGlotaT('spa.chat.scheduling.reviewInformation');
  const typeMessageHereText = useGlotaT('spa.chat.scheduling.typeMessageHere');
  const cancelText = useGlotaT('spa.common.cancel');
  const previousText = useGlotaT('spa.chat.scheduling.previous');
  const scheduleText = useGlotaT('spa.chat.scheduling.schedule');
  const nextText = useGlotaT('spa.common.next');

  return (
    <Modal
      isOpen
      size="lg"
      style={{ width: '650px' }}
      className="change-modal-body"
    >
      <ModalHeader toggle={cancelScheduling}>
        <span>{titleText}</span>
      </ModalHeader>
      <ModalBody style={{ paddingBottom: '0', paddingTop: '4px' }}>
        <p className="mb">
          {step === 1
            ? defineDateTimeText
            : reviewInformationText}
        </p>
        <SimpleChannelSelector disabled={step === 2} />
        <div style={{ display: 'flex', gap: '10px', margin: '10px 0' }}>
          <Input
            type="date"
            id="date"
            name="date"
            value={date}
            onChange={handleChange}
            required
            min={getTodayDate()}
            ref={dateRef}
            disabled={step === 2}
          />
          <Input
            type="time"
            id="time"
            name="time"
            value={time}
            onChange={handleChange}
            required
            min={date === getTodayDate() ? getCurrentTime() : '00:00'} // Define a hora mínima para hoje
            ref={timeRef}
            disabled={step === 2}
          />
        </div>

        {(!quickMessagesIsOpen || selectedChannel.type !== 'waba') && (
          <Input
            type="textarea"
            value={messageScheduling}
            onChange={(event) => setMessageScheduling(event.target.value)}
            placeholder={typeMessageHereText}
            style={{ resize: 'none', height: '100px' }}
            disabled={step === 2}
          />
        )}

        <QuickMessageSelectorV2
          showOnlyTemplateOption={true}
          setSelectedQuickMessage={receiveSelectedQuickMessage}
        />
      </ModalBody>
      <ModalFooter
        style={{ display: 'flex', flexDirection: 'row', flexWrap: 'nowrap' }}
      >
        <Button
          outline
          onClick={() => (step === 1 ? cancelScheduling() : prevStep())}
          style={{ width: '100%' }}
        >
          {step === 1 ? cancelText : previousText}
        </Button>
        <Button
          onClick={() => (step === 1 ? nextStep() : handleSubmit())}
          style={{ width: '100%' }}
          disabled={
            !date || !time || !messageScheduling || !isDateTimeValid(date, time)
          }
        >
          {step === 1 ? nextText : scheduleText}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default TaskSecondStep;
